/**
 * Unified Plan Detection Service
 *
 * Centralized logic for determining organization plan types, status, and capabilities.
 * Used by both backend services and frontend (via API endpoints).
 */

import { logger } from "@repo/logs";
import { getActivePlanFromPurchases } from "./helper";

import { getPlanTier } from "../config/plan-tier";

// Re-export for convenience
export { getActivePlanFromPurchases } from "./helper";
export { getPlanTier } from "../config/plan-tier";

/**
 * Plan type categories
 */
export enum PlanCategory {
	FREE = "free",
	BRAND = "brand",
	AGENCY = "agency",
	ENTERPRISE = "enterprise",
	LEGACY = "legacy",
	PAYG = "payg",
	TRIAL = "trial",
}

/**
 * Subscription status values
 * Note: We use the functions from helper.ts for backward compatibility
 */
export const VALID_SUBSCRIPTION_STATUSES = [
	"active",
	"trialing",
	"past_due",
] as const;
// Note: "expired" is for promotional trials that have ended - users need to subscribe
// Note: "converted" is for promotional trials where user upgraded to a real plan
export const INVALID_SUBSCRIPTION_STATUSES = [
	"canceled",
	"expired",
	"converted",
	"incomplete",
	"paused",
	"unpaid",
] as const;

export type ValidSubscriptionStatus =
	(typeof VALID_SUBSCRIPTION_STATUSES)[number];
export type InvalidSubscriptionStatus =
	(typeof INVALID_SUBSCRIPTION_STATUSES)[number];
export type SubscriptionStatus =
	| ValidSubscriptionStatus
	| InvalidSubscriptionStatus;

/**
 * Check if a subscription status is valid (allows feature access)
 * Extended to include "past_due" for grace period access
 */
export function isValidSubscriptionStatus(
	status: string | null | undefined,
): boolean {
	if (!status) return false;
	return VALID_SUBSCRIPTION_STATUSES.includes(status as any);
}

/**
 * Check if a subscription status is invalid (blocks feature access)
 */
export function isInvalidSubscriptionStatus(
	status: string | null | undefined,
): boolean {
	if (!status) return false;
	return INVALID_SUBSCRIPTION_STATUSES.includes(status as any);
}

/**
 * Plan detection result - comprehensive information about an organization's plan
 */
export interface PlanInfo {
	// Core identification
	planId: string | null; // e.g., "agencyCore", "brand", "free"
	planCategory: PlanCategory;

	// Status
	isActive: boolean; // Can use features
	subscriptionStatus: string | null; // "active", "trialing", "past_due", "expired", etc.
	subscriptionInterval: string | null; // "month", "quarter", "year"

	// Type checks (convenience)
	isBrand: boolean;
	isAgency: boolean;
	isEnterprise: boolean;
	isLegacy: boolean;
	isTrial: boolean;
	isFree: boolean;
	isPayAsYouGo: boolean;

	// Plan details
	hasActiveSubscription: boolean; // Real Stripe subscription (not trial)
	hasTrialSubscription: boolean; // Promotional trial (active)
	hasExpiredTrial: boolean; // Promotional trial that has ended

	// Additional metadata
	planTier: string | null; // "core", "premium", etc.
	remainingTrialDays?: number; // For trials
}

/**
 * Input type for plan detection
 * Matches what we typically query from the database
 */
export interface OrganizationPlanInput {
	subscriptionPlanType?: string | null;
	subscriptionStatus?: string | null;
	subscriptionInterval?: string | null;
	activeSubscriptionId?: string | null;
	stripeCustomerId?: string | null;
	purchases?: Array<{
		id: string;
		type: string;
		productId: string;
		status: string | null;
	}>;
}

/**
 * Detect plan category from plan ID
 */
export function getPlanCategory(
	planId: string | null | undefined,
): PlanCategory {
	if (!planId) return PlanCategory.FREE;

	const lowerPlanId = planId.toLowerCase();

	// v1 Brand ($29) and v2 Brand ($79)
	if (lowerPlanId === "brand" || lowerPlanId === "brandv2") {
		return PlanCategory.BRAND;
	}

	if (lowerPlanId === "enterprise") {
		return PlanCategory.ENTERPRISE;
	}

	// agencyCore, agencyCoreUsageBased, agency_core, agencyCoreV2, agencyPro
	if (lowerPlanId.startsWith("agency")) {
		return PlanCategory.AGENCY;
	}

	if (
		lowerPlanId.includes("legacy") ||
		lowerPlanId === "core" ||
		lowerPlanId === "pro" ||
		lowerPlanId === "lifetime"
	) {
		return PlanCategory.LEGACY;
	}

	if (lowerPlanId === "paygcredits" || lowerPlanId === "payg") {
		return PlanCategory.PAYG;
	}

	if (lowerPlanId === "free") {
		return PlanCategory.FREE;
	}

	return PlanCategory.FREE;
}

/**
 * Main function: Detect comprehensive plan information for an organization
 *
 * This is the single source of truth for plan detection across the entire application.
 *
 * @param organization - Organization data (can be from session, database query, etc.)
 * @returns Comprehensive plan information
 *
 * @example
 * ```typescript
 * // Backend usage (with full data)
 * const org = await db.organization.findUnique({
 *   where: { id },
 *   include: { purchases: true }
 * });
 * const planInfo = detectOrganizationPlan(org);
 *
 * // Frontend usage (from API response)
 * const planInfo = detectOrganizationPlan(sessionOrganization);
 * ```
 */
export function detectOrganizationPlan(
	organization: OrganizationPlanInput | null | undefined,
): PlanInfo {
	// Default to free plan if no organization
	if (!organization) {
		return {
			planId: null,
			planCategory: PlanCategory.FREE,
			isActive: false,
			subscriptionStatus: null,
			subscriptionInterval: null,
			isBrand: false,
			isAgency: false,
			isEnterprise: false,
			isLegacy: false,
			isTrial: false,
			isFree: true,
			isPayAsYouGo: false,
			hasActiveSubscription: false,
			hasTrialSubscription: false,
			hasExpiredTrial: false,
			planTier: null,
		};
	}

	// Check if has trial subscription (promotional trial without Stripe)
	// Only count as active trial if status is "trialing" (not "expired" or "converted")
	const hasTrialSubscription =
		organization.purchases?.some(
			(p) => p.type === "SUBSCRIPTION" && p.status === "trialing",
		) || false;

	// Check if trial has expired (not converted - that means they upgraded)
	const hasExpiredTrial =
		organization.subscriptionStatus === "expired" ||
		organization.purchases?.some(
			(p) => p.type === "SUBSCRIPTION" && p.status === "expired",
		) ||
		false;

	// Note: "converted" status means user upgraded from trial to real plan
	// These are intentionally NOT counted as trials or expired trials

	// Check if has active Stripe subscription
	const hasActiveSubscription = !!(
		organization.activeSubscriptionId &&
		organization.stripeCustomerId &&
		isValidSubscriptionStatus(organization.subscriptionStatus)
	);

	// Determine plan ID from subscriptionPlanType or purchases
	let planId = organization.subscriptionPlanType || null;

	// If no subscriptionPlanType, try to get it from purchases
	if (
		!planId &&
		organization.purchases &&
		organization.purchases.length > 0
	) {
		const activePlan = getActivePlanFromPurchases(
			organization.purchases as any,
		);
		planId = activePlan?.id || null;
	}

	// Determine plan category
	const planCategory = getPlanCategory(planId);

	// Check if plan is active
	// Active if:
	// 1. Has valid subscription status (active, trialing, past_due), OR
	// 2. Has legacy plan (always active), OR
	// 3. Has trial subscription (promotional trial)
	const hasValidStatus = isValidSubscriptionStatus(
		organization.subscriptionStatus,
	);
	const isLegacy = planCategory === PlanCategory.LEGACY;
	const isActive = hasValidStatus || isLegacy || hasTrialSubscription;

	// Type checks
	const isBrand = planCategory === PlanCategory.BRAND && isActive;
	const isAgency = planCategory === PlanCategory.AGENCY && isActive;
	const isEnterprise = planCategory === PlanCategory.ENTERPRISE && isActive;
	const isTrial = hasTrialSubscription && !hasActiveSubscription;
	const isFree =
		(planCategory === PlanCategory.FREE && !hasTrialSubscription) ||
		(!planId && !hasTrialSubscription);
	const isPayAsYouGo =
		planCategory === PlanCategory.PAYG ||
		(hasActiveSubscription && planCategory === PlanCategory.FREE);

	// Get plan tier
	const planTier = getPlanTier(planId);

	logger.debug("Plan detection result", {
		organizationId: (organization as any).id,
		planId,
		planCategory,
		isActive,
		hasActiveSubscription,
		hasTrialSubscription,
		subscriptionStatus: organization.subscriptionStatus,
	});

	return {
		planId,
		planCategory,
		isActive,
		subscriptionStatus: organization.subscriptionStatus || null,
		subscriptionInterval: organization.subscriptionInterval || null,
		isBrand,
		isAgency,
		isEnterprise,
		isLegacy,
		isTrial,
		isFree,
		isPayAsYouGo,
		hasActiveSubscription,
		hasTrialSubscription,
		hasExpiredTrial,
		planTier,
	};
}

/**
 * Check if organization is on an agency plan (convenience function)
 *
 * @param organization - Organization data
 * @returns true if on active agency plan
 */
export function isAgencyOrganization(
	organization: OrganizationPlanInput | null | undefined,
): boolean {
	const planInfo = detectOrganizationPlan(organization);
	return planInfo.isAgency;
}

/**
 * Check if organization has access to agency features
 * This is more lenient than isAgencyOrganization - includes trials and legacy plans
 *
 * @param organization - Organization data
 * @returns true if can access agency features
 */
export function hasAgencyFeatures(
	organization: OrganizationPlanInput | null | undefined,
): boolean {
	const planInfo = detectOrganizationPlan(organization);
	return planInfo.isAgency || planInfo.isEnterprise || planInfo.isLegacy;
}

/**
 * Get user-friendly plan name
 */
export function getPlanDisplayName(planInfo: PlanInfo): string {
	if (planInfo.isTrial) {
		return `${planInfo.planId ? formatPlanName(planInfo.planId) : "Trial"} (Trial)`;
	}

	if (planInfo.planId) {
		return formatPlanName(planInfo.planId);
	}

	if (planInfo.isFree) {
		return "Free";
	}

	return "Unknown";
}

/**
 * Format plan ID to display name
 */
function formatPlanName(planId: string): string {
	const formatted = planId
		.replace(/([A-Z])/g, " $1") // Add space before capitals
		.replace(/_/g, " ") // Replace underscores with spaces
		.trim();

	return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}
